import { useEffect, useRef, useState } from 'react';
import styles from './MonitorTrends.module.css';

// Trend lanes → monitor channel colours. Lanes with no recorded data are hidden.
const LANES = [
  { keys: ['HR'], label: 'HR', color: '#00E676' },
  { keys: ['SpO2'], label: 'SpO₂', color: '#FFFFFF' },
  { keys: ['NIBP_Sys', 'NIBP_Dia'], label: 'NIBP', color: '#FF1744' },
  { keys: ['ABP_Sys', 'ABP_Dia'], label: 'ABP', color: '#FF1744' },
  { keys: ['EtCO2'], label: 'EtCO₂', color: '#FFD600' },
  { keys: ['RR'], label: 'RR', color: '#B0BEC5' },
  { keys: ['CVP'], label: 'CVP', color: '#26C6DA' },
  { keys: ['Temp'], label: 'Temp', color: '#FFFFFF' },
  { keys: ['BIS'], label: 'BIS', color: '#CF9FFF' },
];

const SAMPLE_COLS = ['HR', 'SpO2', 'NIBP_Sys', 'NIBP_Dia', 'ABP_Sys', 'ABP_Dia', 'EtCO2', 'RR', 'Temp', 'CVP', 'BIS', 'TOF'];
// Only columns that were actually recorded (their channel was active at some point).
const activeCols = (samples) => SAMPLE_COLS.filter((c) => samples.some((s) => s[c] != null));

const hhmmss = (epoch) => {
  const d = new Date(epoch);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

/**
 * Fullscreen trend review for the monitor. Draws the recorded numerics as
 * stacked, auto-scaled line graphs (one lane per parameter) over the captured
 * time span. Data comes from the monitor's own rolling sample buffer.
 */
export default function MonitorTrends({ samples, onClose }) {
  const canvasRef = useRef(null);
  const [view, setView] = useState('graph'); // 'graph' | 'table'
  const cols = activeCols(samples);

  useEffect(() => {
    if (view !== 'graph') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const lanes = LANES.filter((l) => l.keys.some((k) => samples.some((s) => s[k] != null)));
    const cssW = canvas.clientWidth || 900;
    const cssH = canvas.clientHeight || 500;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = cssW * dpr;
    canvas.height = cssH * dpr;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    if (lanes.length === 0 || samples.length < 2) {
      ctx.fillStyle = '#8a93a0';
      ctx.font = "14px 'Barlow', sans-serif";
      ctx.textAlign = 'center';
      ctx.fillText('Collecting trend data…', cssW / 2, cssH / 2);
      return;
    }

    const padL = 54;
    const padR = 16;
    const axisH = 20;
    const laneH = (cssH - axisH) / lanes.length;
    const plotW = cssW - padL - padR;
    const t0 = samples[0].t;
    const tSpan = Math.max(1, samples[samples.length - 1].t - t0);
    const xOf = (t) => padL + ((t - t0) / tSpan) * plotW;
    const font = "11px 'Barlow', sans-serif";

    lanes.forEach((lane, i) => {
      const top = i * laneH;
      const plotTop = top + 16;
      const plotBot = top + laneH - 10;

      let min = Infinity;
      let max = -Infinity;
      samples.forEach((s) => lane.keys.forEach((k) => {
        const v = s[k];
        if (typeof v === 'number') { if (v < min) min = v; if (v > max) max = v; }
      }));
      if (!isFinite(min)) { min = 0; max = 1; }
      if (min === max) { min -= 1; max += 1; }
      const pad = (max - min) * 0.15 || 1;
      min -= pad; max += pad;
      const yOf = (v) => plotBot - ((v - min) / (max - min)) * (plotBot - plotTop);

      ctx.strokeStyle = '#2a2f36';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(padL, plotBot); ctx.lineTo(cssW - padR, plotBot); ctx.stroke();

      ctx.fillStyle = lane.color;
      ctx.font = font;
      ctx.textAlign = 'left';
      ctx.fillText(lane.label, 6, top + 22);
      ctx.fillStyle = '#6a7480';
      ctx.textAlign = 'right';
      ctx.fillText(String(Math.round(max)), padL - 6, plotTop + 4);
      ctx.fillText(String(Math.round(min)), padL - 6, plotBot);

      lane.keys.forEach((k, ki) => {
        ctx.strokeStyle = lane.color;
        ctx.globalAlpha = ki === 0 ? 1 : 0.55;
        ctx.lineWidth = 1.75;
        ctx.beginPath();
        let started = false;
        samples.forEach((s) => {
          const v = s[k];
          if (typeof v !== 'number') return;
          const x = xOf(s.t); const y = yOf(v);
          if (!started) { ctx.moveTo(x, y); started = true; } else ctx.lineTo(x, y);
        });
        ctx.stroke();
        ctx.globalAlpha = 1;
      });
    });

    // Time axis
    ctx.fillStyle = '#6a7480';
    ctx.font = font;
    ctx.textAlign = 'left';
    ctx.fillText(hhmmss(t0), padL, cssH - 6);
    ctx.textAlign = 'right';
    ctx.fillText(hhmmss(samples[samples.length - 1].t), cssW - padR, cssH - 6);
  }, [samples, view]);

  const span = samples.length >= 2 ? `${hhmmss(samples[0].t)} – ${hhmmss(samples[samples.length - 1].t)}` : '';

  return (
    <div className={styles.overlay}>
      <div className={styles.head}>
        <span className={styles.title}>Trends</span>
        <div className={styles.toggle}>
          <button className={view === 'graph' ? styles.tabOn : styles.tab} onClick={() => setView('graph')}>Graph</button>
          <button className={view === 'table' ? styles.tabOn : styles.tab} onClick={() => setView('table')}>Table</button>
        </div>
        <span className={styles.span}>{samples.length} samples · {span}</span>
        <button className={styles.close} onClick={onClose}>✕ Close</button>
      </div>

      {view === 'graph' ? (
        <canvas ref={canvasRef} className={styles.canvas} />
      ) : (
        <div className={styles.tableWrap}>
          {samples.length === 0 ? (
            <div className={styles.empty}>No trend data yet.</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr><th>Time</th>{cols.map((c) => <th key={c}>{c}</th>)}</tr>
              </thead>
              <tbody>
                {samples.slice().reverse().map((s, i) => (
                  <tr key={i}>
                    <td className={styles.mono}>{hhmmss(s.t)}</td>
                    {cols.map((c) => <td key={c} className={styles.mono}>{s[c] ?? ''}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
